import { useState, useCallback, useEffect, useRef } from 'react';

// Hooks
import { useAuth }           from './hooks/useAuth';
import { useStats }          from './hooks/useStats';
import { usePlayerData }     from './hooks/usePlayerData';
import { useGameEngine }     from './hooks/useGameEngine';
import { useCatchAnimation } from './hooks/useCatchAnimation';
import { useDirectory }      from './hooks/useDirectory';

// Components
import GameStyles    from './components/GameStyles';
import MainMenu      from './components/MainMenu';
import GameBoard     from './components/GameBoard';
import CatchScreen   from './components/CatchScreen';
import InventoryView from './components/InventoryView';
import ShopView      from './components/ShopView';
import DirectoryView from './components/DirectoryView';

export default function App() {
  // --- Navigation ---
  const [view,           setView]           = useState('menu');
  const [selectedFish,   setSelectedFish]   = useState(null);
  const [hasPlayedToday, setHasPlayedToday] = useState(false);

  // Bridge refs to break circular dependencies
  const updateDirectoryRef = useRef(null);
  const loadCloudDirectoryRef  = useRef(null);
  const onLoginRef         = useRef(null);
  const onLogoutRef        = useRef(null);

  // ----------------------------------------------------------------
  // 1. AUTH (Moved to top to provide 'user' to all subsequent hooks)
  // ----------------------------------------------------------------
  const { user, handleLogin, handleLogout: _handleLogout } = useAuth({
    onLogin:  useCallback((firebaseUser) => onLoginRef.current?.(firebaseUser), []),
    onLogout: useCallback(() => onLogoutRef.current?.(), []),
  });

  // ----------------------------------------------------------------
  // 2. STATS
  // ----------------------------------------------------------------
  const { stats, setStats, updateStats } = useStats({ user });

  // ----------------------------------------------------------------
  // 3. PLAYER DATA (Now successfully takes the 'user' prop)
  // ----------------------------------------------------------------
  const {
    gold,
    level,
    inventory,
    isProcessingBuy,
    fetchUserData,
    resetPlayerData,
    keepFish,
    handleSellFish,
    handlePurchaseLevel,
  } = usePlayerData({ user, setStats, setHasPlayedToday, updateDirectoryRef, loadCloudDirectoryRef });

  // ----------------------------------------------------------------
  // 4. CATCH ANIMATION
  // ----------------------------------------------------------------
  const {
    catchStep,
    setCatchStep,
    currentCatch,
    setCurrentCatch,
    hasCaughtToday,
    setHasCaughtToday,
    generateCatch,
    debugCatch,
  } = useCatchAnimation({ level, stats, updateDirectoryRef });

  // ----------------------------------------------------------------
  // 5. GAME ENGINE (Now successfully takes the 'user' prop)
  // ----------------------------------------------------------------
  const {
    gridWidth,
    gridHeight,
    maxLives,
    todayStr,
    timeLeft,
    handleStartFishing,
    fishPos,
    obstacles,
    cleared,
    flags,
    lives,
    gameState,
    isFlaggingMode,
    setIsFlaggingMode,
    showHelp,
    setShowHelp,
    closeHelp,
    pulsingTiles,
    activeSnagTile,
    handleTileClick: _handleTileClick,
    resetGameBoard,
    getIsSelectable,
  } = useGameEngine({ user, stats, updateStats, generateCatch, setHasPlayedToday });

  // ----------------------------------------------------------------
  // 6. DIRECTORY
  // ----------------------------------------------------------------
  const { directory, updateDirectory, loadCloudDirectory, resetDirectory } = useDirectory({ user });

  // ----------------------------------------------------------------
  // 7. WIRE UP AUTH REF EXTENSIONS
  // ----------------------------------------------------------------
  useEffect(() => {
    onLoginRef.current = (firebaseUser) => {
      fetchUserData(firebaseUser.uid);
      resetGameBoard();
    };
    onLogoutRef.current = () => {
      resetPlayerData();
      resetGameBoard();
      setHasPlayedToday(false); // Explicitly reset gate on logout
    };
  }, [fetchUserData, resetGameBoard, resetPlayerData]);

  // Sync the real updateDirectory into the bridge ref
  useEffect(() => {
    updateDirectoryRef.current = updateDirectory;
  }, [updateDirectory]);

  // Sync the real loadCloudDirectory into the bridge ref so fetchUserData
  // can call it once the cloud fishDirectory data has been retrieved.
  useEffect(() => {
    loadCloudDirectoryRef.current = loadCloudDirectory;
  }, [loadCloudDirectory]);

  // Wrap handleLogout to also reset the directory
  const handleLogout = useCallback(() => {
    _handleLogout();
    resetDirectory();
  }, [_handleLogout, resetDirectory]);

  // ----------------------------------------------------------------
  // TILE CLICK COORDINATOR
  // ----------------------------------------------------------------
  const handleTileClick = useCallback((x, y) => {
    const result = _handleTileClick(x, y);
    if (result?.fish) setCurrentCatch(result.fish);
  }, [_handleTileClick, setCurrentCatch]);

  // ----------------------------------------------------------------
  // STABLE CALLBACKS FOR GAME BOARD
  // ----------------------------------------------------------------
  const handleToggleFlag = useCallback(() => setIsFlaggingMode(f => !f), [setIsFlaggingMode]);
  const handleShowHelp   = useCallback(() => setShowHelp(true),          [setShowHelp]);
  const handleCatch      = useCallback(() => {
    setHasCaughtToday(true);
    setCatchStep(0);
    setView('catch');
  }, [setHasCaughtToday, setCatchStep]);

  const handleStartFishingClick = useCallback(
    () => handleStartFishing(setView),
    [handleStartFishing]
  );

  // ----------------------------------------------------------------
  // STABLE CALLBACKS FOR CATCH SCREEN
  // ----------------------------------------------------------------
  const handleKeepFish = useCallback((fish) => {
    keepFish(fish);
    setView('menu');
  }, [keepFish]);

  const handleSellFromCatch = useCallback((fish) => {
    handleSellFish(fish);
    setView('menu');
  }, [handleSellFish]);

  // ----------------------------------------------------------------
  // STABLE CALLBACKS FOR INVENTORY
  // ----------------------------------------------------------------
  const handleSellFromInventory = useCallback((fish) => {
    handleSellFish(fish);
    setSelectedFish(null);
  }, [handleSellFish]);

  const handleBackToMenu    = useCallback(() => setView('menu'), []);
  const handleViewInventory = useCallback(() => {
    setSelectedFish(null);
    setView('inventory');
  }, []);

  // ----------------------------------------------------------------
  // DEBUG TOOL — dev only
  // ----------------------------------------------------------------
  useEffect(() => {
    if (window.location.hostname === 'localhost') {
      window.debugCatch = debugCatch(setView);
    }
  }, [debugCatch]);

  // ----------------------------------------------------------------
  // RENDER
  // ----------------------------------------------------------------
  return (
    <div
      className="crt-container"
      style={{
        display:        'flex',
        justifyContent: 'center',
        background:     'linear-gradient(to bottom, #001a33, #05070a)',
        color:          'white',
        minHeight:      '100vh',
        padding:        '20px',
        fontFamily:     "'Press Start 2P', cursive",
        touchAction:    'manipulation',
        position:       'relative',
        overflow:       'hidden',
      }}
    >
      <GameStyles />

      {view === 'menu' && (
        <MainMenu
          hasPlayedToday={hasPlayedToday}
          user={user}
          todayStr={todayStr}
          timeLeft={timeLeft}
          onStartFishing={handleStartFishingClick}
          onLogin={handleLogin}
          onLogout={handleLogout}
          onNavigate={(destination) =>
            destination === 'inventory' ? handleViewInventory() : setView(destination)
          }
        />
      )}

      {view === 'game' && (
        <GameBoard
          gridWidth={gridWidth}
          gridHeight={gridHeight}
          maxLives={maxLives}
          lives={lives}
          stats={stats}
          level={level}
          timeLeft={timeLeft}
          cleared={cleared}
          flags={flags}
          fishPos={fishPos}
          obstacles={obstacles}
          gameState={gameState}
          isFlaggingMode={isFlaggingMode}
          showHelp={showHelp}
          hasCaughtToday={hasCaughtToday}
          pulsingTiles={pulsingTiles}
          activeSnagTile={activeSnagTile}
          getIsSelectable={getIsSelectable}
          onTileClick={handleTileClick}
          onToggleFlag={handleToggleFlag}
          onShowHelp={handleShowHelp}
          onCloseHelp={closeHelp}
          onCatch={handleCatch}
          onBack={handleBackToMenu}
        />
      )}

      {view === 'catch' && currentCatch && (
        <CatchScreen
          currentCatch={currentCatch}
          catchStep={catchStep}
          setCatchStep={setCatchStep}
          onKeep={handleKeepFish}
          onSell={handleSellFromCatch}
        />
      )}

      {view === 'inventory' && (
        <InventoryView
          inventory={inventory}
          gold={gold}
          selectedFish={selectedFish}
          onSelectFish={setSelectedFish}
          onSell={handleSellFromInventory}
          onBack={handleBackToMenu}
        />
      )}

      {view === 'directory' && (
        <DirectoryView
          directory={directory}
          onBack={handleBackToMenu}
        />
      )}

      {view === 'shop' && (
        <ShopView
          gold={gold}
          level={level}
          isProcessingBuy={isProcessingBuy}
          onPurchaseLevel={handlePurchaseLevel}
          onBack={handleBackToMenu}
        />
      )}

    </div>
  );
}